// libs/fetch.ts
import { getJwtToken } from "./utils";

/**
 * Fetch data with optional Authorization header and customizable timeout.
 *
 * @param url - The endpoint URL.
 * @param params - Fetch parameters.
 * @param includeAuth - Whether to include the Authorization header.
 * @param timeout - Optional timeout in milliseconds (default is 8000ms).
 * @returns The JSON response.
 */
export default async function fetchData(
  url: string,
  params: RequestInit = {},
  includeAuth: boolean = true,
  timeout: number = 60000 // Default to 8 seconds
): Promise<any> {
  const jwt = getJwtToken();
  console.log('Fetching URL:', url);
  console.log('API_URL from env:', process.env.NEXT_PUBLIC_API_URL);
  const headers: HeadersInit = includeAuth && jwt !== undefined
    ? {
        ...params?.headers,
        Authorization: `Bearer ${jwt}`,
        "Accept-Language": "en",
      }
    : {
        ...params?.headers,
        "Accept-Language": "en",
      };

  const paramsExtended: RequestInit = {
    ...params,
    headers,
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout); // Use the provided timeout

  try {
    const res = await fetch(url, { ...paramsExtended, signal: controller.signal });

    clearTimeout(timeoutId); // Clear the timeout if request completes in time

    // Handle non-OK responses
    if (!res.ok) throw await res.json();

    // Return the JSON response
    return await res.json();
  } catch (error: any) {
    clearTimeout(timeoutId); // Ensure timeout is cleared in case of error

    // Check if the error was caused by a timeout
    if (error.name === 'AbortError') {
      throw { detail: 'Request timed out' };
    }

    // Rethrow other errors (like server responses)
    throw error;
  }
}
